import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search } from "lucide-react";
import { SeasonSelect } from "@/components/materials/SeasonSelect";

const PAGE = 30;
const KINDS = [["taglio", "Tessuti"], ["confezione", "Accessori"]];
const when = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
const low = (x) => (x || "").toLowerCase();

// Riepilogo di quanto \u00e8 stato scalato da ogni lotto (Taglio = tessuti, Confezione = accessori)
export const StockMovesLog = () => {
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["materials-stock-moves"], queryFn: async () => (await api.get("/materials/stock-moves")).data, staleTime: 0 });
  const { data: ms } = useQuery({ queryKey: ["material-seasons"], queryFn: async () => (await api.get("/materials/seasons")).data });
  const [seasonPick, setSeason] = useState(null);
  const season = seasonPick || ms?.current || "tutti";
  const seasonOpts = [...new Set([...(ms?.options || []), ...rows.map((r) => r.season).filter(Boolean)])];
  const [kind, setKind] = useState("taglio");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const words = low(q).split(/\s+/).filter(Boolean);
  const list = rows.filter((r) => r.stage === kind && (season === "tutti" || r.season === season) && words.every((w) => [r.name, r.color, r.supplier, r.product_name, r.batch_color, r.batch_number].some((x) => low(x).includes(w))));
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const p = Math.min(page, pages - 1);
  const totals = Object.values(list.reduce((a, r) => { const k = `${r.material_id}`; a[k] = a[k] || { name: `${r.name} ${r.color || ""}`.trim(), unit: r.unit, qty: 0 }; a[k].qty += r.qty || 0; return a; }, {}));
  return (
    <Card className="overflow-hidden" data-testid="stock-moves-log">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-6 py-4">
        <div>
          <h3 className="font-display text-lg font-semibold">Scarichi dai lotti <span className="text-sm font-normal text-muted-foreground">({list.length})</span></h3>
          <p className="text-xs text-muted-foreground">Il tessuto si scala quando il lotto passa in Taglio, gli accessori quando passa in Confezione. Se il lotto torna indietro, la riga sparisce perché il materiale è tornato in magazzino.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Cerca materiale, articolo, lotto..." className="h-9 w-72 pl-9" data-testid="stock-moves-search" />
          </div>
          <SeasonSelect value={season} options={seasonOpts} current={ms?.current} onChange={(s) => { setSeason(s); setPage(0); }} testid="stock-moves-season" />
          <div className="flex gap-1 rounded-md bg-secondary p-1 text-xs">
            {KINDS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setKind(k); setPage(0); }} data-testid={`stock-moves-kind-${k}`}
                className={`rounded px-3 py-1 transition-colors ${kind === k ? "bg-background font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {totals.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 border-b border-border/60 px-6 py-3 text-xs" data-testid="stock-moves-totals">
          <span className="text-muted-foreground">Totale scalato:</span>
          {totals.map((t) => <span key={t.name}>{t.name} <span className="font-semibold">{num(t.qty)} {t.unit}</span></span>)}
        </div>
      )}
      {isLoading ? <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <Table>
          <TableHeader><TableRow><TableHead>Quando</TableHead><TableHead>Lotto</TableHead><TableHead>Materiale</TableHead><TableHead className="text-right">Scalato</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground" data-testid="stock-moves-empty">{words.length ? "Nessuno scarico corrisponde alla ricerca." : season !== "tutti" ? `Nessuno scarico nella stagione ${season}.` : "Nessuno scarico ancora."}</TableCell></TableRow>
            ) : list.slice(p * PAGE, (p + 1) * PAGE).map((r, i) => (
              <TableRow key={`${r.batch_id}-${r.stage}-${r.material_id}-${i}`} data-testid={`stock-move-row-${r.batch_number}`}>
                <TableCell className="whitespace-nowrap text-sm">{when(r.date)}</TableCell>
                <TableCell><span className="font-medium">{r.product_name}</span> <span className="text-muted-foreground">{r.batch_color}</span>
                  <span className="block font-mono-erp text-[11px] text-muted-foreground">{r.batch_number}{r.cut_ticket_number ? ` \u00b7 ${r.cut_ticket_number}\u00b0 tagliato` : ""}</span></TableCell>
                <TableCell>{r.name} <span className="text-muted-foreground">{r.color}</span><span className="block text-[11px] text-muted-foreground">{r.supplier}</span></TableCell>
                <TableCell className="text-right font-semibold">-{num(r.qty)} {r.unit}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-6 py-3 text-sm">
          <Button size="sm" variant="outline" disabled={p === 0} onClick={() => setPage(p - 1)} data-testid="stock-moves-prev">Precedente</Button>
          <span className="text-muted-foreground">Pagina {p + 1} di {pages}</span>
          <Button size="sm" variant="outline" disabled={p >= pages - 1} onClick={() => setPage(p + 1)} data-testid="stock-moves-next">Successiva</Button>
        </div>
      )}
    </Card>
  );
};
